import { baseMidwifeFields } from '../../collections/fields/practice'
import { stateField } from '../../fields'
import { Field, FileDBValue, ShortcutArgs, WithAlternate } from '../../types'
import { addressToString, formatDropdownValue } from '../data'
import { formatPhoneNumber, getFieldFromPath } from '../fields'
import { resolveProviderClaimsAddress } from '../providers'
import { getFullName } from '../users'
import { getAddressValue } from './address'
import { getFormattedFieldValue } from './utils'

const getMidwifeNameValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  switch (path) {
    case 'fullName':
      return getFullName(shortcutArgs?.practice)
    case 'fname':
      return shortcutArgs?.practice?.fname
    case 'lname':
      return shortcutArgs?.practice?.lname
    case 'name':
      return shortcutArgs?.practice?.name
    default:
      return undefined
  }
}

const getMidwifeNpiValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const practice = shortcutArgs?.practice
  switch (path) {
    case 'referringNpi':
      return practice?.npi
    case 'billingNpi':
      return practice?.isGroup ? practice?.group?.npi : practice?.npi
    default:
      return undefined
  }
}

const getClaimsAddress = (path: string, shortcutArgs: ShortcutArgs) => {
  const { practice } = shortcutArgs ?? {}
  const resolvedClaimsAddress = resolveProviderClaimsAddress(practice)
  return getAddressValue('claimsAddress', path, resolvedClaimsAddress)
}

const getCorporateAddress = (path: string, shortcutArgs: ShortcutArgs) => {
  const { practice } = shortcutArgs ?? {}
  const corporateAddress = practice?.corporateAddress
  return getAddressValue('corporateAddress', path, corporateAddress)
}

const getFirstServicingAddress = (path: string, shortcutArgs: ShortcutArgs) => {
  const { practice } = shortcutArgs ?? {}
  const [firstAddress] =
    practice?.servicingAddresses && Array.isArray(practice.servicingAddresses)
      ? practice.servicingAddresses
      : []
  return getAddressValue('servicingAddress', path, firstAddress)
}

const getMidwifeAddressValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const [firstSegment] = path.split('.')
  switch (firstSegment) {
    case 'claimsAddress':
      return getClaimsAddress(path, shortcutArgs)
    case 'corporateAddress':
      return getCorporateAddress(path, shortcutArgs)
    case 'servicingAddress':
      return getFirstServicingAddress(path, shortcutArgs)
    default:
      return undefined
  }
}

const getMidwifeCollaborativePhysicianValue = (
  path: string,
  shortcutArgs: ShortcutArgs,
): string | undefined => {
  const { practice } = shortcutArgs ?? {}
  const [indexStr, fieldName] = path.split('.')
  if (!indexStr) return undefined
  const index = parseInt(indexStr, 10)
  const npiValue = practice?.collaborativePhysicians?.[index]?.npi as any
  if (Number.isNaN(index)) return undefined
  switch (fieldName) {
    case 'npi':
      return typeof npiValue === 'string' ? npiValue : (npiValue as WithAlternate<string>)?.main
    case 'name':
      return practice?.collaborativePhysicians?.[index]?.name
    default:
      return undefined
  }
}

const getMidwifeAffiliatedHospitalValue = (
  path: string,
  shortcutArgs: ShortcutArgs,
): string | undefined => {
  const { practice } = shortcutArgs ?? {}
  const [indexStr, fieldName] = path.split('.')
  if (!indexStr) return undefined
  const index = parseInt(indexStr, 10)
  const hospitalAddress = practice?.affiliatedHospitals?.[index]?.serviceAddress
  const hospitalPhone = practice?.affiliatedHospitals?.[index]?.phone
  if (Number.isNaN(index)) return undefined
  switch (fieldName) {
    case 'name':
      return practice?.affiliatedHospitals?.[index]?.name
    case 'npi':
      return practice?.affiliatedHospitals?.[index]?.npi
    case 'phone':
      return hospitalPhone ? formatPhoneNumber(hospitalPhone) : undefined
    case 'address':
      return hospitalAddress ? addressToString(hospitalAddress) : undefined
    case 'address2':
      return hospitalAddress ? addressToString(hospitalAddress, true) : undefined
    default:
      return undefined
  }
}

const getMidwifeLicenseValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const { practice } = shortcutArgs ?? {}
  const [indexStr] = path.split('.')
  if (!indexStr) return undefined
  const index = parseInt(indexStr, 10)
  if (Number.isNaN(index)) return undefined
  const license = practice?.licenses?.[index]
  if (!license) return undefined
  const licenseState = formatDropdownValue(license.state, stateField.options)
  const credentials =
    Object.entries(license.credentials ?? {})
      .filter(([, v]) => v)
      .map(([k]) => k) || 'None'
  return `${licenseState} license #: ${license.number} | Credentials: ${credentials}`
}

const getReferringMedicaidIdValue = (
  path: string,
  shortcutArgs: ShortcutArgs,
): string | undefined => {
  const { practice } = shortcutArgs ?? {}
  const [indexStr] = path.split('.')
  if (!indexStr) return undefined
  const index = parseInt(indexStr, 10)
  if (Number.isNaN(index)) return undefined
  const medicaidId = practice?.medicaidIds?.[index]
  if (!medicaidId) return undefined
  const state = formatDropdownValue(medicaidId.state, stateField.options)
  return `${state} Medicaid ID: ${medicaidId.id}`
}

const getGroupMedicaidIdValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const { practice } = shortcutArgs ?? {}
  const [indexStr] = path.split('.')
  if (!indexStr) return undefined
  const index = parseInt(indexStr, 10)
  if (Number.isNaN(index)) return undefined
  const medicaidId = practice?.group?.medicaidIds?.[index]
  if (!medicaidId) return undefined
  const medicaidState = formatDropdownValue(medicaidId.state, stateField.options)
  return `${medicaidState} Medicaid ID: ${medicaidId.id}`
}

const getMidwifeMedicaidValue = (path: string, shortcutArgs: ShortcutArgs): string | undefined => {
  const [fieldPath, ...rest] = path.split('.')
  switch (fieldPath) {
    case 'referringMedicaidIds':
      return getReferringMedicaidIdValue(rest.join('.'), shortcutArgs)
    case 'groupMedicaidIds':
      return getGroupMedicaidIdValue(rest.join('.'), shortcutArgs)
    default:
      return undefined
  }
}

const getFallbackMidwifeValue = (
  path: string,
  shortcutArgs: ShortcutArgs,
  fieldType: any,
): string | undefined => {
  const field = getFieldFromPath(baseMidwifeFields, path) ?? {
    type: fieldType,
    placeholder: '',
  }
  return getFormattedFieldValue(field as Field, path, shortcutArgs.assessment?.midwife)
}

export const getMidwifeValue = (
  path: string,
  shortcutArgs: ShortcutArgs,
): string | FileDBValue | undefined => {
  // ex: payments.received.0
  const [, fieldPath, ...rest] = path.split('.')
  switch (fieldPath) {
    case 'name':
      return getMidwifeNameValue(rest.join('.'), shortcutArgs)
    case 'npi':
      return getMidwifeNpiValue(rest.join('.'), shortcutArgs)
    case 'billingTaxId':
      return shortcutArgs?.assessment?.midwife?.taxId
    case 'addresses':
      return getMidwifeAddressValue(rest.join('.'), shortcutArgs)
    case 'collaborativePhysicians':
      return getMidwifeCollaborativePhysicianValue(rest.join('.'), shortcutArgs)
    case 'affiliatedHospitals':
      return getMidwifeAffiliatedHospitalValue(rest.join('.'), shortcutArgs)
    case 'licenses':
      return getMidwifeLicenseValue(rest.join('.'), shortcutArgs)
    case 'logo':
      return shortcutArgs.practice?.logoFile ?? undefined
    case 'phone':
      return shortcutArgs.practice?.phone
        ? formatPhoneNumber(shortcutArgs.practice.phone)
        : undefined
    case 'medicaid':
      return getMidwifeMedicaidValue(rest.join('.'), shortcutArgs)
    default:
      return getFallbackMidwifeValue([fieldPath, ...rest].join('.'), shortcutArgs, fieldPath)
  }
}
